<template>
  <oculis>
    <div class="oculis-overview">
      <!-- Media List / Selector (Now handling Filters & Downloading KPI inside) -->
      <oculis-media-list
        :project="projectData"
        :medias="medias"
        :loading="projectLoading"
        :comparison-loading="comparisonLoading"
        :fixations-loading="fixationsLoading"
        :medias-loading="mediasLoading"
        @selection-change="handleMediaSelection"
        @compare-request="openCompareDialog"
        @add-media="uploadMedia"
        @update-media="changeMedia"
        @add-fixations="uploadFixations"
        @clear-fixations="clearFixations"
        @clear-media="clearMedia"
        @update-ef="updateProject({id: projectData.id})"
      />
      <!-- Main Workspace (Canvas, Toolbar, Sidebar) -->
      <v-fade-transition mode="out-in">
        <oculis-editor
          v-if="selectedItem && selectedItem.media"
          :key="selectedItem.id"
          :selected-item="selectedItem"
          :medias="medias" 
        />
        
        <v-card v-else class="my-4 pa-12 text-center" flat>
          <v-icon size="64" color="grey lighten-2">{{ icons.mdiImageSearch }}</v-icon>
          <div class="text-h6 grey--text text--lighten-1 mt-4">
            Select an image from the list above to begin analysis
          </div>
        </v-card>
      </v-fade-transition>

      <!-- Dialogs -->
      <oculis-compare-dialog
        v-model="showCompareDialog"
        :items-to-compare="compareList"
        :comparison-headers="comparisonHeaders"
        :images-to-compare="imagesToCompare"
      />

    </div>
  </oculis>
</template>

<script>
import { ref, onMounted, watch } from '@vue/composition-api'

// Sub-components
import Oculis from '@/views/apps/oculis/Oculis.vue'
import OculisMediaList from './oculis-media/OculisMediaList.vue'
import OculisEditor from './oculis-editor/OculisEditor.vue'
import OculisCompareDialog from './oculis-dialogs/OculisCompareDialog.vue'
import OculisKpiTableDialog from './oculis-kpi/OculisKpiTableDialog.vue'

import { mdiImageSearch } from '@mdi/js'
import useOculis from '../useOculis'
import { useRouter } from '@core/utils'

export default {
  components: {
    Oculis,
    OculisMediaList,
    OculisEditor,
    OculisCompareDialog,
    OculisKpiTableDialog
  },
  setup() {
    const { route } = useRouter()
    const { 
      projectData, projectLoading, fixationsLoading, mediasLoading,
      fetchMediaComparisonAnalysis, updateProject,
      fetchOculisProject, addMedia, deleteMedia, updateMedia,
      addFixations, deleteFixations,
      comparisonAnalysisData,
      comparisonLoading,
      analysisData, 
      selectedItem
     } = useOculis()

    const medias = ref([])
    const compareList = ref([])
    const comparisonHeaders = ref([])
    const imagesToCompare = ref([])
    
    // Dialog States
    const showCompareDialog = ref(false)

    const init = () => {
      fetchOculisProject({project:route.value.params.id})
      selectedItem.value = {}
    }

    const handleMediaSelection = (selection) => {
      selectedItem.value = selection.item
      compareList.value = selection.selected
    }

    const openCompareDialog = async (list) => {
          imagesToCompare.value = list;
          
          await fetchMediaComparisonAnalysis(
              route.value.params.id,
              list.map(media=>{return media.id}).join()
          )

          const headers = [
              { text: 'AOI Name', value: 'aoi_name', fixed: true, width: '200px', class: 'grey lighten-3', sortable: false }
          ];

          const loadedDataMap = {}; 
          const sampleSizes = {}; 
          const netStructure = {}; 
          const allKeys = new Set(); 
          const allNames = new Set(); 

          list.forEach(img => {
              headers.push({ 
                  text: img.name, 
                  value: `img_${img.id}`, 
                  align: 'center',
                  sortable: false
              });

              const kpis = comparisonAnalysisData.value[img.id];
              loadedDataMap[img.id] = {}
              
              Object.values(kpis.data).forEach(val => {
                  const aoi_name = val.meta.name
                  loadedDataMap[img.id][aoi_name] = val
              });

              sampleSizes[img.id] = kpis.meta.sample_size; 

              Object.entries(kpis.data).forEach(([key, val]) => {
                  const name = val.meta.name;
                  allNames.add(name);
                  
                  if (val.meta && val.meta.type === 'net') {
                      if (!netStructure[name]) netStructure[name] = new Set();
                      
                      if (val.meta.children) {
                          val.meta.children.forEach(childId => {
                              const child = kpis.data[childId]
                              netStructure[name].add(child.meta.name);
                              allNames.add(child.meta.name); 
                          });
                      }
                  }
              });
          });

          comparisonHeaders.value = headers;

          const orderedRows = [];
          const processedChildren = new Set();

          Object.keys(netStructure).forEach(netName => {
              orderedRows.push({ aoi_name: netName, is_net: true, is_child: false });
              netStructure[netName].forEach(childName => {
                  orderedRows.push({ aoi_name: childName, is_net: false, is_child: true });
                  processedChildren.add(childName);
              });
          });
          
          allNames.forEach(key => {
              if (!netStructure[key] && !processedChildren.has(key)) {
                  orderedRows.push({ aoi_name: key, is_net: false, is_child: false });
              }
          });

          const finalItems = orderedRows.map(rowStructure => {
              const rowObj = { ...rowStructure }; 
              
              list.forEach(img => {
                  const imgData = loadedDataMap[img.id];
                  let cellValue = null;

                  if (imgData && imgData[rowObj.aoi_name]) {
                      const itemData = imgData[rowObj.aoi_name];
                      if (itemData.time_slots && itemData.time_slots['100']) {
                          cellValue = itemData.time_slots['100'];
                      } else if (itemData['100']) {
                          cellValue = itemData['100'];
                      }
                  }

                  rowObj[`img_${img.id}_raw`] = {
                      val: cellValue,
                      n: sampleSizes[img.id]
                  };
              });
              
              return rowObj;
          });

          compareList.value = finalItems;
          showCompareDialog.value = true;
      };

    const handleAoiImport = (newZones) => {
        // Implementation
    }

    const uploadMedia = async (file) => {
      try {
        await addMedia(projectData.value.id, file)
        init()
      } catch (error) {
        console.error('Upload failed', error)
      } 
    }

    const changeMedia = async (media) => {
      try {
        await updateMedia(media)
      } catch (error) {
        console.error('Upload failed', error)
      }
    }
    
    const uploadFixations = async (mediaId, file) => {
      try {
        await addFixations(mediaId, file)
        init()
      } catch (error) {
        console.error('Upload failed', error)
      } 
    }

    const clearFixations = async(mediaId) => {
      try {
        await deleteFixations(mediaId)
        init()
      } catch (error) {
        console.error('Deletion failed', error)
      } 
    }
    
    const clearMedia = async(mediaId) => {
      try {
        await deleteMedia(mediaId)
        init()
      } catch (error) {
        console.error('Deletion failed', error)
      } 
    }

    onMounted(() => {
      init()
    })

    watch(
      projectData,
      () => {
        medias.value = JSON.parse(JSON.stringify(projectData.value)).medias || []
    })

    return {
      projectData, projectLoading, medias, selectedItem,
      compareList, imagesToCompare, comparisonHeaders, analysisData,
      showCompareDialog, comparisonLoading, fixationsLoading, mediasLoading,
      handleMediaSelection, openCompareDialog, handleAoiImport,
      updateProject, uploadMedia, changeMedia, uploadFixations, clearFixations, clearMedia,
      icons: { mdiImageSearch }
    }
  }
}
</script>